/* globals */
import React, { useState, useContext } from 'react';
import { useRedirect } from 'react-admin';
import { useHistory } from 'react-router-dom';
import AddIcon from '@material-ui/icons/Add';
import ListItemText from '@material-ui/core/ListItemText';
import Fab from '@material-ui/core/Fab';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '@material-ui/core/Tooltip';
import Snackbar from '@material-ui/core/Snackbar';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { Button } from '@material-ui/core';
import { QuickAppointmentContext, QuickQuoteContext } from '../Context';
import QuickAppointmentLayout from './QuickAppointmentLayout';
import QuickQuoteLayout from './QuickQuoteLayout';
import useStyles from './styles';
import { getZ3PConfigValue } from '../helper';

const QuickActions = () => {
  const redirect = useRedirect();
  const classes = useStyles();
  const history = useHistory();
  const { handleOpen: handleAppointmentOpen, isRecurring } = useContext(
    QuickAppointmentContext,
  );
  const { toggle, handleOpenQuote } = useContext(QuickQuoteContext);
  const [newScheduledWorkID, setNewScheduledWorkID] = useState(null);
  const [quoteURL, setQuoteURL] = useState(null);
  const [menuAnchorElement, setMenuAnchorElement] = useState(null);

  const { is_using_quotes } = getZ3PConfigValue();

  const handleCloseSnackbar = (_, reason) => {
    if (reason === 'clickaway') return;
    setNewScheduledWorkID(null);
    setQuoteURL(null);
  };

  const viewNewAppointment = () => {
    redirect('show', '/scheduled-work', newScheduledWorkID);
    setNewScheduledWorkID(null);
  };

  const viewNewQuote = () => {
    // Remove the '#' at the beginning of the URL
    redirect(quoteURL.slice(1));
    setQuoteURL(null);
  };

  const formatMenuOptionTitle = (string) => {
    const titles = [
      {
        path: '/technician-blurb',
        title: 'Canned Response',
      },
      {
        path: '/health-check',
        title: 'Service Report',
      },
      {
        path: '/employee',
        title: 'User',
      },
      {
        path: '/client-location',
        title: 'Location',
      },
      {
        path: '/pro-list',
        title: 'Pro',
      },
    ];
    const result = titles.find((item) => item.path === string);
    if (result) {
      return result.title;
    }
    let title = string.slice(1).split('-');
    title = title.map((item) => item.charAt(0).toUpperCase() + item.slice(1));
    title = title.join(' ');
    return title;
  };

  const hasCreateRoute = (path) => {
    const routesWithCreateView = [
      '/service-vehicle',
      '/coupon',
      '/service-offering-checklist',
      '/service-offering-description',
      '/service-custom-message',
      '/client-location',
      '/keyosk',
      '/parking',
      '/price',
      '/technician-blurb',
      '/inspection',
      '/health-check',
      '/employee',
      '/schedule-type',
      '/condition-options',
      '/short-url',
      '/customer-address',
      '/vehicle',
      '/lead',
      '/carfax-record',
      '/payment-method',
      '/service-bay',
      '/pro-list',
    ];

    return routesWithCreateView.indexOf(path) !== -1;
  };

  return (
    <div>
      <Snackbar
        open={!!newScheduledWorkID}
        autoHideDuration={8000}
        onClose={handleCloseSnackbar}
        message={
          isRecurring
            ? 'We are creating your repeat appointments. This may take up to a few minutes. We will notify you (see the bell icon) when they are ready.'
            : 'Appointment created!'
        }
        action={
          <>
            <Button color="secondary" onClick={viewNewAppointment}>
              View Details
            </Button>
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={() => setNewScheduledWorkID(null)}
            >
              <CloseIcon />
            </IconButton>
          </>
        }
      />
      <Snackbar
        open={!!quoteURL}
        autoHideDuration={8000}
        onClose={handleCloseSnackbar}
        message="Quote Created!"
        action={
          <>
            <Button color="secondary" onClick={viewNewQuote}>
              View Details
            </Button>
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={() => setQuoteURL(null)}
            >
              <CloseIcon />
            </IconButton>
          </>
        }
      />
      {is_using_quotes || hasCreateRoute(history.location.pathname) ? (
        <Fab
          color="primary"
          aria-label="add"
          className={`${classes.fabIcon} quack_action`}
          data-tour-id="create-appointment"
          onClick={(event) => setMenuAnchorElement(event.currentTarget)}
        >
          <AddIcon />
        </Fab>
      ) : (
        <Tooltip title="Add Appointment">
          <Fab
            color="primary"
            aria-label="add"
            data-tour-id="create-appointment"
            className={`${classes.fabIcon} quack_action`}
            onClick={handleAppointmentOpen}
          >
            <AddIcon />
          </Fab>
        </Tooltip>
      )}
      <Menu
        anchorEl={menuAnchorElement}
        open={Boolean(menuAnchorElement)}
        className="global-quick-link"
        disableScrollLock
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        PopoverClasses={{ paper: classes.strongBoxShadow }}
        onClose={() => setMenuAnchorElement(null)}
      >
        {hasCreateRoute(history.location.pathname) && (
          <MenuItem
            onClick={() => {
              setMenuAnchorElement(null);
              setTimeout(() => {
                history.push(`${history.location.pathname}/create`);
              }, 50);
            }}
          >
            <ListItemText>
              Add {formatMenuOptionTitle(history.location.pathname)}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setMenuAnchorElement(null);
            handleAppointmentOpen();
          }}
        >
          <ListItemText>Add Appointment</ListItemText>
        </MenuItem>
        {is_using_quotes && (
          <MenuItem
            onClick={() => {
              setMenuAnchorElement(null);
              handleOpenQuote();
            }}
          >
            <ListItemText>Create Quote</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <QuickAppointmentLayout setNewScheduledWorkID={setNewScheduledWorkID} />
      {toggle && <QuickQuoteLayout setQuoteURL={setQuoteURL} />}
    </div>
  );
};

export default QuickActions;
